export enum QueryKeyType {
    // Calendar related keys
    CALENDARS = "calendars",
    APPOINTMENTS = "appointments",
    MARK_AS_PENDING = "markAsPending",
    CALENDAR_PLANS = "calendarPlans",
    CALENDAR_CAPACITIES = "calendarCapacities",
    EVENT_TYPES = "eventTypes",
    EVENT_TYPE_SLOTS = "eventTypeSlots",
    BOOKINGS = "bookings",

    // KPI related keys
    KPIS = "kpis",
    DATE = "date",
    DATE_RANGE = "dateRange",
    TEAM_MEMBERS_KPIS = "teamMembersKpis",

    // Call related keys
    CALLS = "calls",
    LIST = "list",
    DETAIL = "detail",
    INFINITE = "infinite",
    ALL_CALLS = "allCalls",
    CALL = "call",

    // Phone number related keys
    PHONE_NUMBERS = "phoneNumbers",
    MY_PHONE_NUMBERS = "myPhoneNumbers",
    AVAILABLE_PHONE_NUMBERS = "availablePhoneNumbers",
    VALIDATE_PHONE = "validatePhone",

    // Team related keys
    TEAMS = "teams",
    TEAM_MEMBERS = "team-members",

    // Schedule related keys (calendar working hours)
    SCHEDULES = "schedules",
    SCHEDULE_BY_USER = "scheduleByUser",
    // Incident related keys
    INCIDENTS = "incidents",
}

export const createQueryKey = {
    // Calendar keys
    calendars: () => [QueryKeyType.CALENDARS] as const,
    appointments: (calendarName?: string) => [QueryKeyType.CALENDARS, QueryKeyType.APPOINTMENTS, calendarName] as const,
    markAsPending: (appointmentId: string) =>
        [QueryKeyType.CALENDARS, QueryKeyType.APPOINTMENTS, QueryKeyType.MARK_AS_PENDING, appointmentId] as const,
    calendarPlansPagination: (calendarId: string, page: number, pageSize: number) =>
        [QueryKeyType.CALENDARS, QueryKeyType.CALENDAR_PLANS, calendarId, "plans", page, pageSize] as const,
    calendarCapacities: () => [QueryKeyType.CALENDARS, QueryKeyType.CALENDAR_CAPACITIES] as const,
    eventTypes: () => [QueryKeyType.EVENT_TYPES] as const,
    eventType: (id: string) => [QueryKeyType.EVENT_TYPES, id] as const,
    eventTypeSlots: (id: string, startDate: string, endDate: string, includeUnavailable?: boolean) =>
        [
            QueryKeyType.EVENT_TYPES,
            QueryKeyType.EVENT_TYPE_SLOTS,
            id,
            startDate,
            endDate,
            includeUnavailable ?? false,
        ] as const,
    bookings: (filters?: Record<string, unknown>) => [QueryKeyType.BOOKINGS, filters ?? {}] as const,

    // KPI keys
    kpis: () => [QueryKeyType.KPIS] as const,
    kpiByDate: (date: string) => [QueryKeyType.KPIS, QueryKeyType.DATE, date] as const,
    kpiByDateRange: (start: unknown, end: unknown) => [QueryKeyType.KPIS, QueryKeyType.DATE_RANGE, start, end] as const,
    teamMembersKpis: () => [QueryKeyType.TEAM_MEMBERS_KPIS] as const,
    teamMembersKpiByDate: (date: string) => [QueryKeyType.TEAM_MEMBERS_KPIS, QueryKeyType.DATE, date] as const,
    teamMembersKpiByDateRange: (start: unknown, end: unknown) =>
        [QueryKeyType.TEAM_MEMBERS_KPIS, QueryKeyType.DATE_RANGE, start, end] as const,

    // Call keys
    calls: () => [QueryKeyType.CALLS] as const,
    callLists: () => [QueryKeyType.CALLS, QueryKeyType.LIST] as const,
    callList: (filters: Record<string, unknown>) => [QueryKeyType.CALLS, QueryKeyType.LIST, { filters }] as const,
    callDetails: () => [QueryKeyType.CALLS, QueryKeyType.DETAIL] as const,
    callDetail: (id: string) => [QueryKeyType.CALLS, QueryKeyType.DETAIL, id] as const,
    callsByPhoneNumber: (phoneNumber: string) =>
        [QueryKeyType.CALLS, QueryKeyType.LIST, "phoneNumber", phoneNumber] as const,
    allCalls: () => [QueryKeyType.CALLS, QueryKeyType.ALL_CALLS] as const,

    // Phone number keys
    phoneNumbers: () => [QueryKeyType.PHONE_NUMBERS] as const,
    myPhoneNumbers: () => [QueryKeyType.MY_PHONE_NUMBERS] as const,
    availablePhoneNumbers: () => [QueryKeyType.AVAILABLE_PHONE_NUMBERS] as const,
    validatePhone: (phoneDigits: string) => [QueryKeyType.VALIDATE_PHONE, phoneDigits] as const,

    // Team keys
    teams: () => [QueryKeyType.TEAMS] as const,
    teamMembers: () => [QueryKeyType.TEAM_MEMBERS] as const,

    // Schedule keys
    schedules: () => [QueryKeyType.SCHEDULES] as const,
    scheduleByUserId: (userId: string) => [QueryKeyType.SCHEDULES, QueryKeyType.SCHEDULE_BY_USER, userId] as const,
    // Incident keys
    incidents: () => [QueryKeyType.INCIDENTS] as const,
    incidentList: (filters?: Record<string, unknown>) =>
        [QueryKeyType.INCIDENTS, QueryKeyType.LIST, { filters }] as const,
};
